import {
  type Correction,
  formatMinor,
  isBlocking,
  REVIEW_SHORTCUTS,
  type ReviewField,
} from '@hvac/contracts';
import { Alert, Button, Card, CardBody, Icon, Spinner } from '@hvac/ui';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { documentDetailQuery, requestDownloadUrl } from '../../shared/api/document-queries.js';
import { sessionQuery } from '../../shared/api/queries.js';
import {
  openReviewItem,
  reviewItemQuery,
  reviewKeys,
  reviewQueueQuery,
  saveReviewDraft,
  submitReview,
} from '../../shared/api/review-queries.js';
import { DocumentViewer } from '../documents/document-viewer.js';
import { ReviewFieldRow } from './review-field-row.js';
import { checkItems, compareItemPaths } from './review-items.js';
import { useReviewKeyboard } from './use-review-keyboard.js';

/**
 * The review screen.
 *
 * Original on the left, extraction on the right, and a keyboard layer over
 * both. The whole design target is a bookkeeper clearing a hundred receipts
 * without reaching for the mouse, so every decision here is measured against
 * one question: does this cost a keystroke that could have been avoided?
 *
 * Nothing on this screen posts anything. Approving records that a human agreed
 * with the fields; turning those into transactions is a later phase, and a
 * button here implying otherwise would be a promise the system cannot keep.
 */

/** Comparable text for a predicted value, so an undo is recognised as one. */
function asText(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

const GROUPS: readonly { title: string; prefix: string }[] = [
  { title: 'Supplier', prefix: 'vendor.' },
  { title: 'Document', prefix: 'identifiers.' },
  { title: 'Dates', prefix: 'dates.' },
  { title: 'Financial', prefix: 'totals.' },
  { title: 'Payment', prefix: 'payment.' },
  { title: 'Items', prefix: 'lineItems[' },
];

const groupIndex = (fieldPath: string) =>
  GROUPS.findIndex((group) => fieldPath.startsWith(group.prefix));

export function ReviewPage() {
  const { itemId } = useParams({ from: '/protected/review/$itemId' });
  // Keyed on the item so navigating to the next document remounts with fresh
  // state, rather than an effect resetting six pieces of state on every
  // navigation, which React's rules of hooks rightly flag as a cascade.
  return <ReviewScreen key={itemId} itemId={itemId} />;
}

function ReviewScreen({ itemId }: { itemId: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: session } = useSuspenseQuery(sessionQuery());
  const organizationId = session.activeOrganizationId ?? '';

  const item = useQuery(reviewItemQuery(organizationId, itemId));
  const queue = useQuery(reviewQueueQuery(organizationId));

  const [focusedIndex, setFocusedIndex] = useState(0);
  const [editingPath, setEditingPath] = useState<string | null>(null);
  // Null until the reviewer changes something here; until then the edits are
  // whatever was saved as a draft last time.
  const [localEdits, setLocalEdits] = useState<Record<string, unknown> | null>(null);
  // Per-field reasons. Collected but not yet promptable from the UI: the
  // contract and the database both carry them, and asking for one on every
  // edit would slow a hundred-receipt morning to a crawl. A "why?" affordance
  // on the fields humans correct most is the right next iteration.
  const [reasons] = useState<Record<string, string>>({});
  const [showHelp, setShowHelp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const draftVersion = useRef(1);

  // Opening starts the clock. Deliberately fire-and-forget: a reviewer should
  // never wait on a metric, and a failed timer is worth less than a fast screen.
  useEffect(() => {
    if (organizationId === '' || itemId === '') return;
    void openReviewItem(organizationId, itemId)
      .then((result) => {
        draftVersion.current = result.draftVersion;
      })
      .catch(() => {
        /* the review still works; only the timing is lost */
      });
  }, [organizationId, itemId]);

  const detail = item.data ?? null;

  // Corrections saved in an earlier sitting are corrections still: they are
  // sent on approval and kept on the next save, not only displayed. (Before,
  // a saved draft showed its values but approving sent the model's.)
  const savedEdits = useMemo(
    () =>
      Object.fromEntries(
        (detail?.fields ?? [])
          .filter((field) => field.draftValue !== undefined)
          .map((field) => [field.fieldPath, field.draftValue]),
      ),
    [detail],
  );
  const edits = localEdits ?? savedEdits;
  const setEdits = useCallback(
    (update: (current: Record<string, unknown>) => Record<string, unknown>) =>
      setLocalEdits((current) => update(current ?? savedEdits)),
    [savedEdits],
  );

  /** Fields with the reviewer's unsaved edits folded in. */
  const fields: ReviewField[] = useMemo(() => {
    if (detail === null) return [];
    return detail.fields.map((field) =>
      Object.hasOwn(edits, field.fieldPath)
        ? { ...field, draftValue: edits[field.fieldPath], state: 'edited' as const }
        : field,
    );
  }, [detail, edits]);

  // In the order the screen draws them, so the keyboard walks down the page:
  // group by group, and the items line by line.
  const visible = useMemo(
    () =>
      fields
        .filter(
          (field) =>
            groupIndex(field.fieldPath) !== -1 &&
            (field.state !== 'empty' || isBlocking(field.state)),
        )
        .map((field, order) => ({ field, order }))
        .sort(
          (a, b) =>
            groupIndex(a.field.fieldPath) - groupIndex(b.field.fieldPath) ||
            (a.field.fieldPath.startsWith('lineItems[') &&
            b.field.fieldPath.startsWith('lineItems[')
              ? compareItemPaths(a.field.fieldPath, b.field.fieldPath)
              : a.order - b.order),
        )
        .map(({ field }) => field),
    [fields],
  );

  const items = useMemo(() => checkItems(fields), [fields]);

  const blocking = visible.filter((field) => isBlocking(field.state));

  const corrections: Correction[] = useMemo(
    () =>
      Object.entries(edits).map(([fieldPath, correctedValue]) => ({
        fieldPath,
        correctedValue,
        ...(reasons[fieldPath] === undefined ? {} : { reason: reasons[fieldPath] }),
      })),
    [edits, reasons],
  );

  /* ---- navigation ------------------------------------------------------- */

  // Memoised: a fresh array every render would re-arm the prefetch effect on
  // every keystroke, refetching the next document over and over.
  const ordering = useMemo(() => queue.data?.items.map((entry) => entry.id) ?? [], [queue.data]);
  const position = ordering.indexOf(itemId);
  const goTo = useCallback(
    (offset: number) => {
      const target = ordering[position + offset];
      if (target !== undefined) {
        void navigate({ to: '/review/$itemId', params: { itemId: target } });
      }
    },
    [navigate, ordering, position],
  );

  // The next document is prefetched the moment this one opens, so pressing J
  // feels like nothing happened rather than like a page load.
  useEffect(() => {
    const next = ordering[position + 1];
    if (next === undefined || organizationId === '') return;
    void queryClient.prefetchQuery(reviewItemQuery(organizationId, next));
  }, [organizationId, ordering, position, queryClient]);

  /* ---- actions ---------------------------------------------------------- */

  const saveDraft = useMutation({
    mutationFn: () =>
      saveReviewDraft(organizationId, itemId, {
        fieldValues: edits,
        version: draftVersion.current,
      }),
    onSuccess: (result) => {
      draftVersion.current = result.version;
      setError(null);
    },
    onError: (caught) => setError(caught instanceof Error ? caught.message : 'Could not save.'),
  });

  const submit = useMutation({
    mutationFn: (outcome: 'approved' | 'rejected' | 'escalated') =>
      submitReview(organizationId, itemId, {
        outcome,
        version: detail?.version ?? 1,
        corrections,
        ...(outcome === 'rejected' ? { notes: 'Rejected during review.' } : {}),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: reviewKeys.all(organizationId) });
      // Straight to the next document. Returning to the queue after every
      // approval would cost two keystrokes and a re-read of the list.
      const next = ordering[position + 1];
      void navigate(
        next === undefined
          ? { to: '/review' }
          : { to: '/review/$itemId', params: { itemId: next } },
      );
    },
    onError: (caught) =>
      setError(caught instanceof Error ? caught.message : 'Could not submit this review.'),
  });

  const commitEdit = useCallback(
    (fieldPath: string, value: string | null) => {
      setEdits((current) => {
        const field = detail?.fields.find((candidate) => candidate.fieldPath === fieldPath);
        // Typing a value back to what the model said is not a correction, and
        // recording it as one would teach Phase 7 that the model was wrong.
        if (field !== undefined && asText(field.predictedValue) === (value ?? '')) {
          const { [fieldPath]: _removed, ...rest } = current;
          return rest;
        }
        return { ...current, [fieldPath]: value };
      });
      setEditingPath(null);
    },
    [detail, setEdits],
  );

  useReviewKeyboard({
    enabled: editingPath === null && !showHelp,
    actions: {
      onApprove: () => submit.mutate('approved'),
      onReject: () => submit.mutate('rejected'),
      onSaveDraft: () => saveDraft.mutate(),
      onEditFocused: () => {
        const field = visible[focusedIndex];
        if (field !== undefined) setEditingPath(field.fieldPath);
      },
      onNextField: () => setFocusedIndex((index) => Math.min(index + 1, visible.length - 1)),
      onPreviousField: () => setFocusedIndex((index) => Math.max(index - 1, 0)),
      onNextDocument: () => goTo(1),
      onPreviousDocument: () => goTo(-1),
      onCancel: () => {
        setEditingPath(null);
        setShowHelp(false);
      },
      onToggleHelp: () => setShowHelp((open) => !open),
    },
  });

  /* ---- render ----------------------------------------------------------- */

  if (item.isPending) {
    return (
      <div className="flex justify-center py-16">
        <Spinner className="size-6 text-brand-600" label="Loading the review" />
      </div>
    );
  }

  if (item.isError || detail === null) {
    return (
      <Alert tone="danger" title="Could not open this review">
        {item.error instanceof Error ? item.error.message : 'It may already be completed.'}
      </Alert>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="truncate text-lg font-semibold text-content">{detail.title}</h1>
          <p className="text-xs text-content-muted">
            {position >= 0 ? `${position + 1} of ${ordering.length} · ` : ''}
            {detail.detail}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setShowHelp((open) => !open)}>
            Shortcuts (?)
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => saveDraft.mutate()}
            disabled={saveDraft.isPending}
          >
            {saveDraft.isPending ? 'Saving…' : 'Save draft (S)'}
          </Button>
          <Button variant="secondary" size="sm" onClick={() => submit.mutate('rejected')}>
            Reject (R)
          </Button>
          <Button
            size="sm"
            onClick={() => submit.mutate('approved')}
            disabled={submit.isPending || blocking.length > 0}
            title={
              blocking.length > 0
                ? `${blocking.map((field) => field.label).join(', ')} still need attention`
                : undefined
            }
          >
            {submit.isPending ? 'Approving…' : 'Approve (A)'}
          </Button>
        </div>
      </header>

      {error !== null ? <Alert tone="danger">{error}</Alert> : null}

      {blocking.length > 0 ? (
        <Alert tone="warning">
          {blocking.map((field) => field.label).join(', ')} still{' '}
          {blocking.length === 1 ? 'needs' : 'need'} attention before this can be approved.
        </Alert>
      ) : null}

      {showHelp ? (
        <Card>
          <CardBody>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm sm:grid-cols-3">
              {REVIEW_SHORTCUTS.map((shortcut) => (
                <div key={shortcut.action} className="flex items-baseline gap-2">
                  <dt className="shrink-0">
                    <kbd className="rounded border border-border bg-surface-sunken px-1.5 py-0.5 font-mono text-xs">
                      {shortcut.keys}
                    </kbd>
                  </dt>
                  <dd className="text-content-muted">{shortcut.description}</dd>
                </div>
              ))}
            </dl>
          </CardBody>
        </Card>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <DocumentPane organizationId={organizationId} documentId={detail.documentId} />

        <div className="flex max-h-[42rem] flex-col overflow-y-auto rounded-[var(--radius-surface)] border border-border bg-surface">
          {detail.issues.filter((issue) => issue.fieldPath === null).length > 0 ? (
            <div className="border-b border-border p-3">
              {detail.issues
                .filter((issue) => issue.fieldPath === null)
                .map((issue) => (
                  <Alert
                    key={issue.id}
                    tone={issue.severity === 'error' ? 'danger' : 'warning'}
                    className="mb-2 last:mb-0"
                  >
                    {issue.message}
                  </Alert>
                ))}
            </div>
          ) : null}

          <div className="flex items-start gap-2 border-b border-border bg-brand-50 px-3 py-2 text-xs text-brand-700">
            <Icon name="pencil" className="mt-0.5 size-3.5 shrink-0" />
            <p>
              Read by AI, which can misread. <strong>Click any value to correct it</strong> (or
              press E), checking it against the document. Your correction is what gets saved, and
              this supplier's next receipt learns from it.
              {corrections.length > 0
                ? ` ${String(corrections.length)} correction${corrections.length === 1 ? '' : 's'} so far.`
                : ''}
            </p>
          </div>

          {GROUPS.map((group) => {
            const groupFields = visible.filter((field) => field.fieldPath.startsWith(group.prefix));
            if (groupFields.length === 0) return null;

            return (
              <section key={group.prefix}>
                <h2 className="sticky top-0 z-10 bg-surface px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-content-subtle">
                  {group.title}
                </h2>
                {group.prefix === 'lineItems[' && items.agrees !== null ? (
                  <p
                    className={[
                      'px-3 pb-1.5 text-xs',
                      items.agrees ? 'text-success' : 'text-warning',
                    ].join(' ')}
                  >
                    {items.agrees
                      ? `${String(items.lineCount)} item${items.lineCount === 1 ? '' : 's'} add up to ${formatMinor(items.itemsMinor.toString())}, matching the receipt.`
                      : `${String(items.lineCount)} item${items.lineCount === 1 ? '' : 's'} add up to ${formatMinor(items.itemsMinor.toString())}, but the ${items.againstLabel ?? 'total'} is ${formatMinor((items.againstMinor ?? 0n).toString())}. One is probably misread: click it to correct.`}
                  </p>
                ) : null}
                {groupFields.map((field) => {
                  const index = visible.indexOf(field);
                  return (
                    <ReviewFieldRow
                      key={field.fieldPath}
                      field={field}
                      isFocused={index === focusedIndex}
                      isEditing={editingPath === field.fieldPath}
                      onFocus={() => setFocusedIndex(index)}
                      onStartEdit={() => {
                        setFocusedIndex(index);
                        setEditingPath(field.fieldPath);
                      }}
                      onCommit={(value) => commitEdit(field.fieldPath, value)}
                    />
                  );
                })}
              </section>
            );
          })}

          <footer className="mt-auto border-t border-border px-3 py-2 text-xs text-content-subtle">
            Read by {detail.modelName ?? 'the extraction model'}
            {detail.promptVersion === null ? '' : `, prompt v${detail.promptVersion}`}
            {corrections.length > 0
              ? ` · ${corrections.length} correction${corrections.length === 1 ? '' : 's'} pending`
              : ''}
          </footer>
        </div>
      </div>
    </div>
  );
}

/**
 * The original, beside the extraction.
 *
 * Reuses the Phase 3 viewer rather than a second implementation: zoom, rotate,
 * fit and page navigation already work there, and two viewers would drift.
 */
function DocumentPane({
  organizationId,
  documentId,
}: {
  organizationId: string;
  documentId: string | null;
}) {
  const document = useQuery({
    ...documentDetailQuery(organizationId, documentId ?? ''),
    enabled: documentId !== null,
  });

  const download = useQuery({
    queryKey: ['review-file', organizationId, documentId],
    queryFn: () => requestDownloadUrl(organizationId, documentId ?? '', { expiresInSeconds: 900 }),
    enabled: documentId !== null && document.isSuccess,
    staleTime: 840_000,
  });

  if (documentId === null) {
    return (
      <Alert tone="info" title="No document">
        This review is not attached to a document.
      </Alert>
    );
  }

  if (download.isPending) {
    return (
      <div className="flex h-[42rem] items-center justify-center rounded-[var(--radius-surface)] border border-border">
        <Spinner className="size-6 text-brand-600" label="Opening the document" />
      </div>
    );
  }

  if (download.data === undefined) {
    return <Alert tone="danger">The original could not be opened.</Alert>;
  }

  return (
    <div className="h-[42rem]">
      <DocumentViewer
        url={download.data.downloadUrl}
        mimeType={download.data.mimeType}
        filename={download.data.filename}
      />
    </div>
  );
}
